/**
 * Care task service - assigned tasks for the care recipient.
 */
import { Platform } from 'react-native';
import type { CareTask } from '../types/care';

const COLLECTION = 'care';
const TASKS_DOC = 'tasks';

function getDb(): Promise<any> {
  if (Platform.OS === 'web') {
    return import('./unifiedAuthService').then((m) => m.getWebDb());
  }
  const { initializeFirebase, getMobileFirestore } = require('./unifiedAuthService');
  if (!initializeFirebase()) return Promise.resolve(null);
  const getFs = getMobileFirestore();
  return Promise.resolve(getFs ? getFs() : null);
}

function tasksPath(recipientId: string): string {
  return `users/${recipientId}/${COLLECTION}/${TASKS_DOC}`;
}

export const careTaskService = {
  async list(recipientId: string): Promise<CareTask[]> {
    const db = await getDb();
    if (!db) return [];
    try {
      if (Platform.OS === 'web') {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, tasksPath(recipientId)));
        const data = snap.exists() ? snap.data() : null;
        const list = (data?.items ?? []) as CareTask[];
        return list.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
      }
      const ref = db.collection('users').doc(recipientId).collection(COLLECTION).doc(TASKS_DOC);
      const snap = await ref.get();
      const data = snap.exists ? snap.data() : null;
      const list = (data?.items ?? []) as CareTask[];
      return list.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
    } catch (e) {
      console.warn('careTaskService.list error:', e);
      return [];
    }
  },

  /** Upcoming/pending tasks (not done), optionally up to N days ahead. */
  getUpcoming(tasks: CareTask[], fromDate: string, daysAhead: number): CareTask[] {
    const to = new Date(fromDate);
    to.setDate(to.getDate() + daysAhead);
    const toStr = to.toISOString().slice(0, 10);
    return tasks.filter((t) => t.status === 'pending' && t.dueDate >= fromDate && t.dueDate <= toStr);
  },

  /** Overdue: pending and dueDate < today. */
  getOverdue(tasks: CareTask[], todayDate: string): CareTask[] {
    return tasks.filter((t) => t.status === 'pending' && t.dueDate < todayDate);
  },

  async add(recipientId: string, task: Omit<CareTask, 'id' | 'createdAt' | 'status'>): Promise<CareTask> {
    const list = await this.list(recipientId);
    const newTask: CareTask = {
      ...task,
      status: 'pending',
      id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: new Date().toISOString(),
    };
    list.push(newTask);
    await this.saveAll(recipientId, list);
    return newTask;
  },

  async update(recipientId: string, taskId: string, patch: Partial<CareTask>): Promise<void> {
    const list = await this.list(recipientId);
    const idx = list.findIndex((t) => t.id === taskId);
    if (idx === -1) return;
    list[idx] = { ...list[idx], ...patch };
    await this.saveAll(recipientId, list);
  },

  async markDone(recipientId: string, taskId: string): Promise<void> {
    await this.update(recipientId, taskId, { status: 'done', completedAt: new Date().toISOString() });
  },

  async markMissed(recipientId: string, taskId: string): Promise<void> {
    await this.update(recipientId, taskId, { status: 'missed' });
  },

  async delete(recipientId: string, taskId: string): Promise<void> {
    const list = (await this.list(recipientId)).filter((t) => t.id !== taskId);
    await this.saveAll(recipientId, list);
  },

  async saveAll(recipientId: string, items: CareTask[]): Promise<void> {
    const db = await getDb();
    if (!db) return;
    const payload = { items, updatedAt: new Date().toISOString() };
    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, tasksPath(recipientId)), payload, { merge: true });
      } else {
        await db.collection('users').doc(recipientId).collection(COLLECTION).doc(TASKS_DOC).set(payload, { merge: true });
      }
    } catch (e) {
      console.warn('careTaskService.saveAll error:', e);
      throw e;
    }
  },
};
